import React, { useRef, useState } from 'react'
import { Save, Upload, Trash, File, Cloud, LogOut, Bot, LogIn, Unlock, CheckCircle2 } from 'lucide-react'
import { useGameplayContext } from '../../contexts/gameplayContext'
import { useAuth } from '../../contexts/authContext'
import {
  saveGameDataLocal,
  loadGameDataLocal,
  deleteGameData,
  exportGameData,
  isProductionBuild,
  checkUnlockRules,
  isUnlockRuleMet,
  isDemo,
  stateMigrations,
  isInIframe
} from '../../utils'
import { toast } from 'sonner'
import {
  AutomationEnum,
  GamePlayData,
  JobEnum,
  LocationEnum,
  UpgradeEnum,
  QuestEnum,
  CareerPath,
  GenericKeyEnum,
  SkillEnum
} from '../../types'
import {
  Checkbox,
  Input,
  Select,
  SelectItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button
} from '@nextui-org/react'
import { JOBS, CAREERS } from '../../constants/jobs'
import LOCATIONS from '../../constants/locations'
import { UPGRADES } from '../../constants/upgrades'
import { QUESTS } from '../../constants/quests'
import SKILLS from '@/constants/skills'
import StripeCheckout from '../StripeCheckout'

const AUTOMATION_TYPES = [
  'rest',
  'work',
  'surf',
  'level_up',
  'job_change',
  'money_save',
  'location_change',
  'purchase',
  'stat_reach',
  'quest_complete'
] as const

const formatLastSaveTime = (isoString?: string): string => {
  if (!isoString) return 'Never'
  const date = new Date(isoString)
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
    Math.round((date.getTime() - Date.now()) / (1000 * 60)),
    'minutes'
  )
}

const AutomationsSection: React.FC<{ gameData: GamePlayData; setGameData: (data: GamePlayData) => void }> = ({
  gameData,
  setGameData
}) => {
  const handleAutomationToggle = (automation: AutomationEnum) => {
    setGameData({
      ...gameData,
      automations: {
        ...gameData.automations,
        [automation]: {
          ...gameData.automations[automation],
          isActive: !gameData.automations[automation].isActive
        }
      }
    })
  }

  const handleAutomationKeyChange = (automation: AutomationEnum, key: GenericKeyEnum) => {
    setGameData({
      ...gameData,
      automations: {
        ...gameData.automations,
        [automation]: {
          ...gameData.automations[automation],
          key
        }
      }
    })
  }

  const handleAutomationValueChange = (automation: AutomationEnum, value: number) => {
    setGameData({
      ...gameData,
      automations: {
        ...gameData.automations,
        [automation]: {
          ...gameData.automations[automation],
          value
        }
      }
    })
  }

  const getUnlockedSkills = () => {
    return Object.entries(gameData.skills)
      .filter(([_, skill]) => skill.level > 0)
      .map(([key]) => key)
  }

  const getUnlockedJobs = () => {
    const unlockedJobs: JobEnum[] = []
    Object.values(CAREERS).forEach((careerPath) => {
      if (careerPath.unlockRules.every((rule) => isUnlockRuleMet(rule, gameData))) {
        careerPath.jobs.forEach((jobKey, index) => {
          const job = JOBS[jobKey as JobEnum]
          const isJobUnlocked = job.unlockRules.every((rule) => isUnlockRuleMet(rule, gameData))
          const isPreviousJobUnlocked =
            index === 0 ||
            JOBS[careerPath.jobs[index - 1] as JobEnum].unlockRules.every((rule) => isUnlockRuleMet(rule, gameData))

          if (isJobUnlocked || (isPreviousJobUnlocked && !isJobUnlocked)) {
            unlockedJobs.push(jobKey as JobEnum)
          }
        })
      }
    })
    return unlockedJobs
  }

  const getUnlockedLocations = () => {
    return Object.entries(LOCATIONS)
      .filter(([key]) => {
        if (key === 'mediterranean') return true
        if (isDemo()) {
          return key === 'france' && gameData.completedChapters.includes('mediterranean')
        } else {
          return (
            gameData.completedChapters.includes(key as LocationEnum) ||
            gameData.completedQuests.some((questKey) => QUESTS[questKey].effect.locationUnlocked === key)
          )
        }
      })
      .map(([key]) => key as LocationEnum)
  }

  const getUnlockedUpgrades = () => {
    return Object.entries(UPGRADES)
      .filter(
        ([key, upgrade]) =>
          checkUnlockRules(upgrade.visibilityRules, gameData) && !gameData.upgrades[key as UpgradeEnum]?.isPurchased
      )
      .map(([key]) => key as UpgradeEnum)
  }

  const getUnlockedQuests = () => {
    return gameData.activeMainQuest ? [gameData.activeMainQuest] : []
  }

  const renderAutomationControls = (automation: AutomationEnum) => {
    const automationState = gameData.automations[automation]
    if (!automationState.isUnlocked) return null
    let keyInput = null
    let valueInput = null

    switch (automation) {
      case 'level_up':
        const unlockedSkills = getUnlockedSkills()
        const currentSkill = automationState.key
        keyInput = (
          <Select
            label="Skill"
            selectedKeys={currentSkill ? [currentSkill] : []}
            onChange={(e) => handleAutomationKeyChange(automation, e.target.value as GenericKeyEnum)}
            className="w-40"
            classNames={{
              trigger: 'h-8',
              value: 'text-small',
              label: 'text-tiny'
            }}
            size="sm"
          >
            {unlockedSkills.map((skill) => (
              <SelectItem key={skill} value={skill}>
                {SKILLS[skill as SkillEnum].name}
              </SelectItem>
            ))}
          </Select>
        )
        valueInput = (
          <Input
            type="number"
            label="Level"
            value={automationState.value?.toString() || ''}
            onChange={(e) => handleAutomationValueChange(automation, parseInt(e.target.value))}
            className="w-24"
            size="sm"
          />
        )
        break
      case 'job_change':
        const unlockedJobs = getUnlockedJobs()
        const currentJob = automationState.key
        keyInput = (
          <Select
            label="Job"
            selectedKeys={currentJob ? [currentJob] : []}
            onChange={(e) => handleAutomationKeyChange(automation, e.target.value as GenericKeyEnum)}
            className="w-48"
            classNames={{
              trigger: 'h-8',
              value: 'text-small',
              label: 'text-tiny'
            }}
            size="sm"
          >
            {unlockedJobs.map((key) => {
              const job = JOBS[key as JobEnum]
              const isJobUnlocked = job.unlockRules.every((rule) => isUnlockRuleMet(rule, gameData))
              return (
                <SelectItem key={key} value={key}>
                  {isJobUnlocked ? JOBS[key as JobEnum].name : '?? Unknown Job ??'}
                </SelectItem>
              )
            })}
          </Select>
        )
        break
      case 'location_change':
        const unlockedLocations = getUnlockedLocations()
        const currentLocation = automationState.key
        keyInput = (
          <Select
            label="Location"
            selectedKeys={currentLocation ? [currentLocation] : []}
            onChange={(e) => handleAutomationKeyChange(automation, e.target.value as GenericKeyEnum)}
            className="w-48"
            classNames={{
              trigger: 'h-8',
              value: 'text-small',
              label: 'text-tiny'
            }}
            size="sm"
          >
            {unlockedLocations.map((key) => (
              <SelectItem key={key} value={key}>
                {LOCATIONS[key as LocationEnum].name}
              </SelectItem>
            ))}
          </Select>
        )
        break
      case 'purchase':
        const unlockedUpgrades = getUnlockedUpgrades()
        const currentUpgrade = automationState.key
        keyInput = (
          <Select
            label="Upgrade"
            selectedKeys={currentUpgrade ? [currentUpgrade] : []}
            onChange={(e) => handleAutomationKeyChange(automation, e.target.value as GenericKeyEnum)}
            className="w-48"
            classNames={{
              trigger: 'h-8',
              value: 'text-small',
              label: 'text-tiny'
            }}
            size="sm"
          >
            {unlockedUpgrades.map((key) => (
              <SelectItem key={key} value={key}>
                {UPGRADES[key].name}
              </SelectItem>
            ))}
          </Select>
        )
        break
      case 'money_save':
        valueInput = (
          <Input
            type="number"
            label="Amount"
            value={automationState.value?.toString() || ''}
            onChange={(e) => handleAutomationValueChange(automation, parseInt(e.target.value))}
            className="w-32"
            size="sm"
          />
        )
        break
      case 'stat_reach':
        const currentStat = automationState.key
        keyInput = (
          <Select
            label="Stat"
            selectedKeys={currentStat ? [currentStat] : []}
            onChange={(e) => handleAutomationKeyChange(automation, e.target.value as GenericKeyEnum)}
            className="w-48"
            classNames={{
              trigger: 'h-8',
              value: 'text-small',
              label: 'text-tiny'
            }}
            size="sm"
          >
            {Object.keys(gameData.stats).map((key) => (
              <SelectItem key={key} value={key}>
                {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
              </SelectItem>
            ))}
          </Select>
        )
        valueInput = (
          <Input
            type="number"
            label="Value"
            value={automationState.value?.toString() || ''}
            onChange={(e) => handleAutomationValueChange(automation, parseInt(e.target.value))}
            className="w-32"
            size="sm"
          />
        )
        break
      case 'quest_complete':
        const unlockedQuests = getUnlockedQuests()
        const currentQuest = automationState.key
        keyInput = (
          <Select
            label="Quest"
            selectedKeys={currentQuest ? [currentQuest] : []}
            onChange={(e) => handleAutomationKeyChange(automation, e.target.value as GenericKeyEnum)}
            className="w-48"
            classNames={{
              trigger: 'h-8',
              value: 'text-small',
              label: 'text-tiny'
            }}
            size="sm"
          >
            {unlockedQuests.map((key) => (
              <SelectItem key={key} value={key}>
                {QUESTS[key].name}
              </SelectItem>
            ))}
          </Select>
        )
        break
    }

    return (
      <div key={automation} className="flex items-center gap-4 py-2">
        <div className="w-32">
          <Checkbox
            isSelected={automationState.isActive}
            onValueChange={() => handleAutomationToggle(automation)}
            size="sm"
            color="primary"
            isDisabled={automation === 'rest'}
          >
            <span className="capitalize">{automation.replace('_', ' ')}</span>
          </Checkbox>
        </div>
        <div className="flex items-center gap-2 min-h-[40px]">
          {keyInput}
          {valueInput}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-3 shadow-lg">
      <div className="flex items-center gap-2 mb-3">
        <Bot className="w-6 h-6 text-white" />
        <h3 className="text-xl font-semibold text-white">Automations</h3>
      </div>
      <div className="space-y-2">{AUTOMATION_TYPES.map((automation) => renderAutomationControls(automation))}</div>
    </div>
  )
}

export const LoginDialog: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { user, signOut, signInWithProvider, signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setIsLoggingIn(true)
      await signIn(email)
      toast.success('Check your email for the login link!')
      onClose()
    } catch (error) {
      console.error('Error logging in:', error)
      toast.error('Error sending login link. Please try again.')
    } finally {
      setIsLoggingIn(false)
    }
  }

  const handleProviderLogin = async (provider: 'google' | 'discord') => {
    try {
      await signInWithProvider(provider)
      onClose()
    } catch (error) {
      console.error('Error logging in:', error)
      toast.error(`Error signing in with ${provider}. Please try again.`)
    }
  }

  // If user is logged in, show simple logout dialog
  if (user?.email) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} placement="center">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1 bg-gray-100">Account Settings</ModalHeader>
          <ModalBody className="space-y-4 py-4">
            <div className="text-center">
              <p className="font-semibold">{user.email}</p>
              <p className="text-sm text-gray-500">Logged in account</p>
            </div>
            <Button
              color="danger"
              onClick={async () => {
                await signOut()
                onClose()
              }}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center justify-center text-lg"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }

  // If user is not logged in, show login options
  return (
    <Modal isOpen={isOpen} onClose={onClose} placement="center">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 bg-gray-100">Log In</ModalHeader>
        <ModalBody className="space-y-4 py-4">
          <div className="text-center text-sm text-gray-500 mb-4">
            Log in to save your progress across devices and activate the full game.
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <Input
              type="email"
              label="Email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button
              type="submit"
              isLoading={isLoggingIn}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center justify-center text-lg"
            >
              {isLoggingIn ? 'Sending...' : 'Send Magic Link'}
            </Button>
          </form>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-default-100 text-default-600">Or continue with</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Button
              onClick={() => handleProviderLogin('google')}
              className="bg-white hover:bg-gray-100 text-gray-800 font-bold py-2 px-4 rounded transition-colors inline-flex items-center justify-center text-lg border border-gray-300"
            >
              <img
                src="https://developers.google.com/identity/images/g-logo.png"
                alt="Google logo"
                className="w-5 h-5 mr-2"
              />
              Google
            </Button>
            <Button
              onClick={() => handleProviderLogin('discord')}
              className="bg-white hover:bg-gray-100 text-gray-800 font-bold py-2 px-4 rounded transition-colors inline-flex items-center justify-center text-lg border border-gray-300"
            >
              <img src="/assets/discord.svg" alt="Discord logo" className="h-5 mr-2" />
            </Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export const ActivateDialog: React.FC<{ isOpen: boolean; onClose: () => void; openLoginDialog: () => void }> = ({
  isOpen,
  onClose,
  openLoginDialog
}) => {
  const { user, checkGameActivation } = useAuth()
  const [isProcessingRequest, setIsProcessingRequest] = useState(false)

  const handleSuccess = async () => {
    await checkGameActivation()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} placement="center" size="lg">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 bg-gray-100">Activate Full Game</ModalHeader>
        <ModalBody className="py-6">
          {user ? (
            <StripeCheckout onSuccess={handleSuccess} onCancel={onClose} />
          ) : (
            <div className="text-center p-4">
              <p className="text-gray-700 mb-4">Please log in to activate the full game.</p>
              <Button
                color="primary"
                onClick={() => {
                  onClose()
                  // Open login dialog after closing this one
                  setTimeout(() => {
                    openLoginDialog()
                  }, 300)
                }}
              >
                Login to Continue
              </Button>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export const SettingsScreen: React.FC = () => {
  const { gameData, setGameData, saveToCloud, loadFromCloud } = useGameplayContext()
  const { user, signIn, signOut, loading, checkGameActivation, isFullGameActivated } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false)
  const [isActivateDialogOpen, setIsActivateDialogOpen] = useState(false)

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const importedData = stateMigrations(JSON.parse(e.target?.result as string))
          saveGameDataLocal(importedData)
          setGameData(importedData)
          toast.success('Game data imported successfully!')
        } catch (error) {
          console.error('Error importing game data:', error)
          toast.error('Error importing game data. Please check the file format.')
        }
      }
      reader.readAsText(file)
    }
    event.target.value = ''
  }

  const handleExport = () => {
    const { url, filename } = exportGameData()

    const link = document.createElement('a')
    link.href = url
    link.download = filename

    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleCloudSave = async () => {
    try {
      setIsSaving(true)
      const success = await saveToCloud()
      if (success) {
        toast.success('Game saved successfully!')
      } else {
        toast.error('Failed to save game to cloud')
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleCloudLoad = async () => {
    try {
      setIsLoading(true)
      const success = await loadFromCloud()
      if (success) {
        toast.success('Game loaded from cloud successfully!')
      } else {
        toast.error('Failed to load game from cloud')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const renderUserInfo = () => {
    if (isInIframe()) {
      return (
        <div className="space-y-4">
          <div className="flex flex-col">
            <span className="text-gray-200">{gameData.username}</span>
            <span className="text-gray-200 text-sm">(demo version)</span>
          </div>

          <div className="p-5 bg-blue-900/50 rounded-lg text-white w-full">
            <p className="mb-3 text-lg">This is Demo version of the GOAT Surfer game. Play the full game on:</p>
            <a
              href="https://goatsurfer.goaat.xyz/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded transition-colors w-fit mb-3"
            >
              <img src="/assets/the_goat_surfer_sm.png" alt="GOAT Surfer Logo" className="w-10 h-10" />
              goatsurfer.goaat.xyz
            </a>
            <p className="text-sm text-gray-300">
              You can export your game save from here and import it on the full game page.
            </p>
            <button
              className="mt-4 bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center"
              onClick={handleExport}
            >
              <File className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <div className="flex flex-col">
          <span className={user?.email ? 'text-emerald-200 font-semibold' : 'text-gray-200'}>{gameData.username}</span>
          <span className="text-gray-200 text-sm">{user?.email ? `(${user.email})` : '(anonymous user)'}</span>
        </div>
        {!user?.email && (
          <div className="flex flex-col">
            <span className={'text-white font-bold'}>Log In to Activate Full Game and enable cloud saves</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setIsLoginDialogOpen(true)}
            color={user?.email ? 'danger' : 'primary'}
            className="bg-blue-500 hover:bg-blue-600 text-white text-base font-semibold py-2 px-4 rounded transition-colors inline-flex items-center"
            data-login-button="true"
          >
            {user?.email ? (
              <>
                <LogOut className="w-5 h-5 mr-2" />
                Log Out
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5 mr-2" />
                Log In
              </>
            )}
          </Button>

          {isFullGameActivated ? (
            <div className="bg-emerald-600 text-white font-bold py-2 px-4 rounded inline-flex items-center text-lg">
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Full Game
            </div>
          ) : (
            <Button
              onClick={() => setIsActivateDialogOpen(true)}
              isDisabled={!user?.email}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Unlock className="w-5 h-5 mr-2" />
              Activate Full Game
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-white drop-shadow-md">Settings ⚙️</h2>

      <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-3 shadow-lg space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Local Storage Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">💾</span>
              <h3 className="text-xl font-semibold text-white">Save / Load Game</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center"
                onClick={() => {
                  saveGameDataLocal(gameData)
                  handleCloudSave()
                }}
              >
                <Save className="w-4 h-4 mr-2" />
                Local Save
              </button>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center"
                onClick={() => setGameData(loadGameDataLocal())}
              >
                <Upload className="w-4 h-4 mr-2" />
                Local Load
              </button>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center disabled:opacity-50"
                onClick={handleCloudLoad}
                disabled={isLoading}
              >
                <Upload className="w-4 h-4 mr-2" />
                {isLoading ? 'Loading...' : 'Cloud Load'}
              </button>
              <button
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center"
                onClick={() => deleteGameData()}
              >
                <Trash className="w-4 h-4 mr-2" />
                Delete
              </button>
            </div>
          </div>

          {/* File Management Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">📁</span>
              <h3 className="text-xl font-semibold text-white">Import / Export Game</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center"
                onClick={handleExport}
              >
                <File className="w-4 h-4 mr-2" />
                Export
              </button>
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".json" className="hidden" />
              <button
                className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center"
                onClick={handleImportClick}
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </button>
            </div>
          </div>

          {!isProductionBuild() ? (
            <>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">☁️</span>
                  <h3 className="text-xl font-semibold text-white">Cloud Save Account</h3>
                </div>

                {!loading && (
                  <div className="space-y-4">
                    {renderUserInfo()}

                    <div className="flex gap-2">
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center disabled:opacity-50"
                        onClick={handleCloudSave}
                        disabled={isSaving}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {isSaving ? 'Saving...' : 'Cloud Save'}
                      </button>
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center disabled:opacity-50"
                        onClick={handleCloudLoad}
                        disabled={isLoading}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {isLoading ? 'Loading...' : 'Cloud Load'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            renderUserInfo()
          )}
        </div>

        <div className="mt-6 text-sm text-gray-300 space-y-1">
          <p>
            💾 Last saved: {formatLastSaveTime(gameData.lastSaveTime)} ({gameData.lastSaveTime})
          </p>
        </div>
      </div>

      {/* Automations Section */}
      <AutomationsSection gameData={gameData} setGameData={setGameData} />

      {/* Discord Section */}
      <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-3 shadow-lg">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">💬</span>
          <h3 className="text-xl font-semibold text-white">Join Discord</h3>
        </div>
        <p className="text-white mb-3">Join our community to be first to know about updates and share your feedback!</p>
        <a
          href="https://discord.gg/VzEeEycYHa"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white hover:bg-gray-100 py-2 px-4 rounded transition-colors inline-flex items-center justify-center"
        >
          <img src="/assets/discord.svg" alt="Join Discord" className="h-8" />
        </a>
      </div>

      <LoginDialog isOpen={isLoginDialogOpen} onClose={() => setIsLoginDialogOpen(false)} />
      <ActivateDialog
        isOpen={isActivateDialogOpen}
        onClose={() => setIsActivateDialogOpen(false)}
        openLoginDialog={() => setIsLoginDialogOpen(true)}
      />
    </div>
  )
}
